import React from 'react';
import { cn } from '@/lib/utils';
import { colors } from '../design-system/colors';

export type StatusType = 'draft' | 'sent' | 'approved' | 'rejected' | 'pending' | 'in_progress' | 'completed' | 'active' | 'inactive' | 'active_client' | 'new_lead' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | 'feedback' | 'revision' | 'in_revision' | 'on_hold' | 'cancelled' | 'cancelledChangeOrder' | 'submitted' | 'ordered' | 'received' | 'open' | 'resolved' | 'under_review' | 'awarded' | 'paid' | 'partially_paid' | 'void' | 'voided' | 'posted' | 'not_started' | 'delayed';
export type PriorityType = 'low' | 'medium' | 'high';
export type RoleType = 'admin' | 'member';

interface GenericTagProps {
  type: 'status' | 'priority' | 'role';
  value: StatusType | PriorityType | RoleType;
  className?: string;
  children?: React.ReactNode; // Optional custom label to override default
}

const statusConfig: Record<StatusType, { label: string; textColor: string; bgColor: string }> = {
  draft: { label: 'Draft', textColor: colors.tagStatusDraft, bgColor: colors.tagStatusDraftBg },
  sent: { label: 'Sent', textColor: colors.tagStatusSent, bgColor: colors.tagStatusSentBg },
  approved: { label: 'Approved', textColor: colors.tagStatusApproved, bgColor: colors.tagStatusApprovedBg },
  rejected: { label: 'Rejected', textColor: colors.tagStatusRejected, bgColor: colors.tagStatusRejectedBg },
  pending: { label: 'Pending', textColor: colors.tagStatusPending, bgColor: colors.tagStatusPendingBg },
  in_progress: { label: 'In Progress', textColor: colors.tagStatusInProgress, bgColor: colors.tagStatusInProgressBg },
  completed: { label: 'Completed', textColor: colors.tagStatusCompleted, bgColor: colors.tagStatusCompletedBg },
  active: { label: 'Active', textColor: colors.tagStatusActive, bgColor: colors.tagStatusActiveBg },
  inactive: { label: 'Inactive', textColor: colors.tagStatusInactive, bgColor: colors.tagStatusInactiveBg },
  // Lead-specific statuses
  active_client: { label: 'Active Client', textColor: colors.tagStatusActiveClient, bgColor: colors.tagStatusActiveClientBg },
  new_lead: { label: 'New Lead', textColor: colors.tagStatusNewLead, bgColor: colors.tagStatusNewLeadBg },
  contacted: { label: 'Contacted', textColor: colors.tagStatusContacted, bgColor: colors.tagStatusContactedBg },
  qualified: { label: 'Qualified', textColor: colors.tagStatusQualified, bgColor: colors.tagStatusQualifiedBg },
  proposal: { label: 'Proposal', textColor: colors.tagStatusProposal, bgColor: colors.tagStatusProposalBg },
  negotiation: { label: 'Negotiation', textColor: colors.tagStatusNegotiation, bgColor: colors.tagStatusNegotiationBg },
  closed_won: { label: 'Closed Won', textColor: colors.tagStatusClosedWon, bgColor: colors.tagStatusClosedWonBg },
  closed_lost: { label: 'Closed Lost', textColor: colors.tagStatusClosedLost, bgColor: colors.tagStatusClosedLostBg },
  // Proposal-specific statuses
  feedback: { label: 'Feedback', textColor: colors.tagStatusFeedback, bgColor: colors.tagStatusFeedbackBg },
  revision: { label: 'Revision', textColor: colors.tagStatusRevision, bgColor: colors.tagStatusRevisionBg },
  // Budget-specific status (same yellow as revision)
  in_revision: { label: 'In Revision', textColor: colors.tagStatusRevision, bgColor: colors.tagStatusRevisionBg },
  // Project-specific statuses
  on_hold: { label: 'On Hold', textColor: colors.tagStatusPending, bgColor: colors.tagStatusPendingBg },
  cancelled: { label: 'Cancelled', textColor: colors.tagStatusCancelled, bgColor: colors.tagStatusCancelledBg },
  // Change order-specific: cancelled with orange color
  cancelledChangeOrder: { label: 'Cancelled', textColor: colors.tagStatusInProgress, bgColor: colors.tagStatusInProgressBg },
  // Material order-specific statuses
  submitted: { label: 'Submitted', textColor: colors.tagStatusSubmitted, bgColor: colors.tagStatusSubmittedBg },
  ordered: { label: 'Ordered', textColor: colors.tagStatusOrdered, bgColor: colors.tagStatusOrderedBg },
  received: { label: 'Received', textColor: colors.tagStatusReceived, bgColor: colors.tagStatusReceivedBg },
  // Warranty-specific statuses
  open: { label: 'Open', textColor: colors.tagStatusOpen, bgColor: colors.tagStatusOpenBg }, // Red for open
  resolved: { label: 'Resolved', textColor: colors.tagStatusResolved, bgColor: colors.tagStatusResolvedBg }, // Green for resolved
  // Bid-specific statuses
  under_review: { label: 'Under Review', textColor: colors.tagStatusUnderReview, bgColor: colors.tagStatusUnderReviewBg }, // Yellow for under review
  awarded: { label: 'Awarded', textColor: colors.tagStatusAwarded, bgColor: colors.tagStatusAwardedBg }, // Green for awarded
  // Bill-specific statuses
  paid: { label: 'Paid', textColor: colors.tagStatusPaid, bgColor: colors.tagStatusPaidBg }, // Green for paid bills
  partially_paid: { label: 'Partially Paid', textColor: colors.tagStatusPartiallyPaid, bgColor: colors.tagStatusPartiallyPaidBg }, // Yellow/orange for partially paid bills
  void: { label: 'Void', textColor: colors.tagStatusVoid, bgColor: colors.tagStatusVoidBg }, // Purple for void bills
  // Purchase order-specific status
  voided: { label: 'Voided', textColor: colors.tagStatusVoid, bgColor: colors.tagStatusVoidBg }, // Purple for voided purchase orders (reuses void colors)
  // Payment-specific status
  posted: { label: 'Posted', textColor: colors.tagStatusPaid, bgColor: colors.tagStatusPaidBg }, // Green for posted payments (same as paid)
  // Gantt/Schedule-specific statuses (Gantt, phase97)
  not_started: { label: 'Not Started', textColor: colors.tagStatusDraft, bgColor: colors.tagStatusDraftBg }, // Gray, same as draft
  delayed: { label: 'Delayed', textColor: colors.tagStatusRejected, bgColor: colors.tagStatusRejectedBg }, // Red, same as rejected
};

const priorityConfig: Record<PriorityType, { label: string; textColor: string; bgColor: string }> = {
  low: { label: 'Low', textColor: colors.tagPriorityLow, bgColor: colors.tagPriorityLowBg },
  medium: { label: 'Medium', textColor: colors.tagPriorityMedium, bgColor: colors.tagPriorityMediumBg },
  high: { label: 'High', textColor: colors.tagPriorityHigh, bgColor: colors.tagPriorityHighBg },
};

const roleConfig: Record<RoleType, { label: string; textColor: string; bgColor: string }> = {
  admin: { label: 'Admin', textColor: colors.tagRoleAdmin, bgColor: colors.tagRoleAdminBg },
  member: { label: 'Member', textColor: colors.tagRoleMember, bgColor: colors.tagRoleMemberBg },
};

/**
 * GenericTag - Standardized tag component for status and priority badges
 * 
 * Features:
 * - Pill shape (rounded-full)
 * - Standard size and padding
 * - Title case labels
 * - Colors from centralized color palette
 * 
 * @example
 * <GenericTag type="status" value="approved" />
 * <GenericTag type="priority" value="high" />
 */
export function GenericTag({ type, value, className, children }: GenericTagProps) {
  const config = type === 'status' 
    ? statusConfig[value as StatusType]
    : type === 'priority'
    ? priorityConfig[value as PriorityType]
    : roleConfig[value as RoleType];

  if (!config) {
    console.warn(`Invalid ${type} value: ${value}`);
    return null;
  }

  // Use custom children if provided, otherwise use default label
  // Ensure proper capitalization (Title Case) - always capitalize first letter of each word
  const displayText = children || config.label;
  const formattedText = typeof displayText === 'string' 
    ? displayText
        .toLowerCase() // First convert to lowercase
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : displayText;

  return (
    <span
      className={cn(
        "generic-tag inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
        className
      )}
      style={{ 
        color: config.textColor,
        backgroundColor: config.bgColor,
        textTransform: 'none',
        fontVariant: 'normal'
      }}
    >
      {formattedText}
    </span>
  );
}

